import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Smartphone, Ban, RotateCcw, CheckSquare, Square } from "lucide-react";

/** iter86.75 — Rivalry Ops → Mobile Access: per-rivalry mobile connection
 * governance (revoke / re-enable / bulk). Scope: TESTER_MOBILE_STANDIN.md §2.2 */
export default function MobileAccessTab() {
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(() => new Set());

    const load = useCallback(async () => {
        try {
            const r = await api.siteMobileAccessList();
            setConnections(r.connections || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    const patch = async (id, active) => {
        try {
            await api.siteMobileAccessPatch(id, active);
            toast.success(active ? "Connection re-enabled" : "Connection revoked");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const bulk = async (active) => {
        try {
            const r = await api.siteMobileAccessBulk(Array.from(selected), active);
            toast.success(`${r.updated} connection(s) ${active ? "re-enabled" : "revoked"}`);
            setSelected(new Set());
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed"); }
    };

    const toggleSel = (id) => {
        const next = new Set(selected);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelected(next);
    };

    const byLeague = connections.reduce((acc, c) => {
        (acc[c.league_name || c.league_id] = acc[c.league_name || c.league_id] || []).push(c);
        return acc;
    }, {});

    return (
        <div className="brutal-card p-5 space-y-4" data-testid="mobile-access-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-cyber" />
                    <h3 className="font-display font-extrabold text-sm uppercase tracking-wider">Mobile Access</h3>
                </div>
                {selected.size > 0 && (
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-400">{selected.size} selected</span>
                        <button data-testid="mobile-access-bulk-revoke-btn" onClick={() => bulk(false)} className="brutal-btn text-xs py-1.5 bg-heat text-black font-bold"><Ban className="w-3 h-3" /> Revoke</button>
                        <button data-testid="mobile-access-bulk-enable-btn" onClick={() => bulk(true)} className="brutal-btn text-xs py-1.5 bg-neon text-black font-bold"><RotateCcw className="w-3 h-3" /> Re-enable</button>
                    </div>
                )}
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
                Every mobile/tester connection into this tenant's rivalries. Revoking severs the rivalry connection
                (the account keeps its login — platform-level account controls live in Commons → Mobile Testers).
            </p>
            {loading && <p className="text-xs text-zinc-500">Loading…</p>}
            {!loading && connections.length === 0 && (
                <p className="text-sm text-zinc-500" data-testid="mobile-access-empty">No mobile connections yet.</p>
            )}
            {Object.entries(byLeague).map(([name, rows]) => (
                <div key={name} className="space-y-1.5">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{name}</p>
                    {rows.map((c) => (
                        <div key={c.membership_id} data-testid={`mobile-access-row-${c.membership_id}`}
                            className={`flex items-center gap-3 p-2.5 border-2 rounded-md ${c.active ? "border-[#333]" : "border-heat/40 opacity-70"}`}>
                            <button onClick={() => toggleSel(c.membership_id)} data-testid={`mobile-access-select-${c.membership_id}`} className="shrink-0">
                                {selected.has(c.membership_id) ? <CheckSquare className="w-4 h-4 text-cyber" /> : <Square className="w-4 h-4 text-zinc-600" />}
                            </button>
                            <div className="flex-1 min-w-0">
                                <span className="font-bold text-sm">{c.tester_username || "anonymous device"}</span>
                                <span className="text-xs text-zinc-400"> → as <strong className="text-white">{c.participant_username || "?"}</strong> ({c.role === "admin" ? "Admin" : "Crewmate"})</span>
                                <span className="text-xs text-zinc-500"> · code <code className="font-mono text-volt">{c.personal_code || "—"}</code> · claimed {String(c.claimed_at || "").slice(0, 10)}</span>
                            </div>
                            <span className={`text-[9px] uppercase tracking-widest font-bold ${c.active ? "text-neon" : "text-heat"}`}>
                                {c.active ? "Active" : "Revoked"}
                            </span>
                            <button
                                data-testid={`mobile-access-toggle-${c.membership_id}`}
                                onClick={() => patch(c.membership_id, !c.active)}
                                className={`brutal-btn-ghost text-xs py-1 px-2 ${c.active ? "text-heat" : "text-neon"}`}>
                                {c.active ? "Revoke" : "Re-enable"}
                            </button>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}
